import { userApi } from '@shared/api-client';
import { useMutation } from '@tanstack/react-query';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useFormError } from '@/hooks/use-form-errors';
import { useSession } from '@/hooks/use-session';
import { ConfirmDialog } from '../components/confirm-dialog';
import { SettingsSection } from '../components/settings-section';

const PasswordSection = () => {
  const { error, captureError, clearError } = useFormError();
  const [localError, setLocalError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);

  const mutation = useMutation({
    mutationFn: userApi.updatePassword,
    onSuccess: () => {
      formRef.current?.reset();
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
    onError: captureError,
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();
    setLocalError(null);
    setSaved(false);

    const form = new FormData(event.currentTarget);
    const newPassword = String(form.get('newPassword'));

    if (newPassword.length < 12) {
      setLocalError('Use at least 12 characters');
      return;
    }

    if (newPassword !== String(form.get('confirmPassword'))) {
      setLocalError('Passwords do not match');
      return;
    }

    mutation.mutate({
      currentPassword: String(form.get('currentPassword')),
      newPassword,
    });
  };

  return (
    <div>
      <form ref={formRef} onSubmit={handleSubmit}>
        <SettingsSection
          title="Password"
          description="Changing your password does not sign you out of other devices."
          footer={
            <>
              {saved ? (
                <span className="mr-auto text-sm text-positive">
                  Password updated
                </span>
              ) : null}
              <Button type="submit" loading={mutation.isPending}>
                Update password
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {error ? <Alert tone="error">{error}</Alert> : null}

            <Field label="Current password" htmlFor="currentPassword">
              <Input
                name="currentPassword"
                type="password"
                autoComplete="current-password"
                required
              />
            </Field>

            <Field
              label="New password"
              htmlFor="newPassword"
              hint="At least 12 characters, with upper and lower case and a number."
            >
              <Input
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
              />
            </Field>

            <Field
              label="Confirm new password"
              htmlFor="confirmPassword"
              error={localError}
            >
              <Input
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
              />
            </Field>
          </div>
        </SettingsSection>
      </form>
    </div>
  );
};

const DeleteAccountSection = () => {
  const navigate = useNavigate();
  const { user } = useSession();
  const { error, captureError, clearError } = useFormError();
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const mutation = useMutation({
    mutationFn: userApi.deleteCurrentUser,
    onSuccess: () => navigate('/signin', { replace: true }),
    onError: (err) => {
      // The API refuses while you are the sole owner of an organization; that
      // message names which one, so it belongs on the page, not in the dialog.
      setDialogOpen(false);
      captureError(err);
    },
  });

  return (
    <>
      <SettingsSection
        title="Delete account"
        description="Removes your account and signs you out everywhere. This cannot be undone."
        footer={
          <Button
            variant="danger"
            onClick={() => {
              clearError();
              setDialogOpen(true);
            }}
          >
            Delete account
          </Button>
        }
      >
        {error ? <Alert tone="error">{error}</Alert> : null}
        {!error ? (
          <p className="text-sm text-ink-muted">
            If you are the only owner of an organization, transfer ownership or
            delete that organization first.
          </p>
        ) : null}
      </SettingsSection>

      <ConfirmDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="Delete your account?"
        description="Your account and personal data will be removed. Organizations you own with other members are not deleted."
        confirmLabel="Delete account"
        confirmationPhrase={user?.email}
        loading={mutation.isPending}
        onConfirm={() => mutation.mutate()}
      />
    </>
  );
};

export const SecurityTab = () => (
  <div className="space-y-6">
    <PasswordSection />
    <DeleteAccountSection />
  </div>
);
